/**
 * Format rules for demographic fields whose shape is fixed by the EBTS, not by the workflow.
 *
 * These are applied by field NAME rather than from the workflow's stored definition, deliberately:
 * workflows are runtime data that site admins edit, and a rule added to the shipped defaults would not
 * reach the configurations already saved. The EBTS format for height is not a per-customer preference —
 * it is the same in every workflow — so it belongs to the field itself.
 */

/**
 * Height is EBTS field 2.027 (HGT): exactly three digits, feet then inches, e.g. 5'9" is 509 and 5'11" is
 * 511.
 *
 * Inches are 00-11: at 12 the value should have carried into the feet digit, and a submission that reads
 * "5 feet 14 inches" is a violation rather than a tall applicant. Feet start at 1, since a height of zero
 * feet is a typo in every case that can reach this form.
 */
export const HEIGHT_PATTERN = /^[1-9](0\d|1[01])$/;

export const HEIGHT_HELP = 'Three digits: feet then inches, 00–11. 5′9″ = 509, 5′11″ = 511, 6′0″ = 600.';

export function isValidHeight(value: string | undefined): boolean {
  return HEIGHT_PATTERN.test((value ?? '').trim());
}

/**
 * Turn what an operator naturally types into the EBTS form, WITHOUT guessing.
 *
 * `5'9"`, `5 9`, `5-9` and `5'09` all clearly mean 509, so they are converted. Two bare digits are not
 * converted: "59" could be 5'9" or a mistyped 509, and silently choosing one would put a wrong height into
 * a submitted EFT — the operator is shown the error instead. Anything it cannot read confidently is
 * returned unchanged, so the field's own validation reports it.
 */
export function normalizeHeight(raw: string | undefined): string {
  const v = (raw ?? '').trim();
  if (!v) return '';
  // Already in EBTS form (or three digits that fail validation — let the validator speak, not this).
  if (/^\d{3}$/.test(v)) return v;

  // feet <separator> inches, where the separator makes the intent explicit.
  const m = v.match(/^(\d)\s*(?:'|’|ft\.?|f|-|\.|\s)\s*(\d{1,2})\s*(?:"|”|in\.?|i)?$/i);
  if (m) {
    const feet = m[1]!;
    const inches = Number(m[2]);
    if (inches <= 11) return `${feet}${String(inches).padStart(2, '0')}`;
  }
  // A lone foot count: 6' is unambiguous.
  const feetOnly = v.match(/^(\d)\s*(?:'|’|ft\.?|f)$/i);
  if (feetOnly) return `${feetOnly[1]}00`;

  return v;
}

/**
 * Keystroke-level constraint for a field with a fixed shape.
 *
 * Height takes digits only, capped at three: it stops a fourth digit or a stray letter reaching the EFT at
 * all, rather than reporting it afterwards. Separators are still accepted so `5'9"` can be typed and
 * normalized on blur — they are stripped only once the value is long enough to be unambiguous.
 */
export function sanitizeFieldInput(name: string, value: string): string {
  if (name !== 'height') return value;
  // Keep the characters normalizeHeight understands while typing; drop anything that cannot appear.
  const kept = value.replace(/[^\d'’"”\-. ftinFTIN]/g, '');
  return /^\d+$/.test(kept) ? kept.slice(0, 3) : kept.slice(0, 8);
}

/** Fixed-shape fields whose value must match a format, checked wherever demographics are edited. */
export function formatInvalid(name: string, value: string | undefined): boolean {
  const v = (value ?? '').trim();
  if (!v) return false; // emptiness is the required-field check's business, not this one's
  if (name === 'height') return !isValidHeight(v);
  return false;
}

/** Message explaining why a fixed-shape value was rejected. */
export function formatMessage(name: string): string | null {
  return name === 'height' ? HEIGHT_HELP : null;
}

/**
 * A standing warning for a field that is safe to leave blank but harmful to guess at.
 *
 * Keyed by field NAME, which lands it on exactly the ATF workflows: `miscNumber` (EBTS 2.017) exists only in
 * `atf4` and `conv-atf4`. Name-based for the same reason as the format rules — workflows are runtime data, and a
 * flag added to the shipped defaults never reaches a configuration a site admin already saved.
 *
 * The misc number is reserved for cases the ATF specifically asks for. A customer filled it in because a labelled
 * empty box invites it, the ATF rejected the submission, and correcting it then produced an EMPTY field 2.017 —
 * which was rejected again. Both failures start with the field looking like something to complete.
 */
export function fieldWarning(name: string): string | null {
  if (name !== 'miscNumber') return null;
  return 'Only use this if the ATF has specifically instructed you to. Otherwise LEAVE IT BLANK — an unrequested miscellaneous number will cause the ATF to reject the submission.';
}
